/**
 * NftSendModal — send one NFT to another account.
 *
 * Mirrors TransferScreen's recipient input, but for a single AtomicAssets
 * asset. Authorized only by the sender; the recipient never co-signs and
 * never needs to pre-approve anything, so there is nothing to check on their
 * side before sending.
 */

import { useEffect, useState } from 'react'
import { getExplorerUrl } from '@properwallet/chain-eos'
import { useWalletStore } from '../store/wallet.store'
import { useAppStore } from '../store/app.store'
import { useTranslation } from '../hooks/useTranslation'
import type { TranslationKey } from '@properwallet/i18n'

export interface NftSendModalProps {
  assetId: string
  nftName: string
  onClose: () => void
}

export default function NftSendModal({ assetId, nftName, onClose }: NftSendModalProps) {
  const { t } = useTranslation()
  const { sendNft, nftSendStatus, clearNftSendStatus, activeSession, navigate } = useWalletStore()
  const { setDiscoveryUrl, selectedExplorer } = useAppStore()
  const [recipient, setRecipient] = useState('')
  const [memo, setMemo] = useState('')

  // Fresh status each time the modal opens for a (possibly different) asset.
  useEffect(() => {
    clearNftSendStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId])

  const sending = nftSendStatus?.state === 'sending'
  const sent = nftSendStatus?.state === 'sent'
  const error = nftSendStatus?.state === 'error' ? nftSendStatus.message : null

  const errorText = error
    ? t(
        (['noRecipient', 'invalidAccount', 'selfTransfer'].includes(error)
          ? `transfer.errors.${error}`
          : 'nft.sendError') as TranslationKey,
      )
    : null

  const handleClose = () => {
    clearNftSendStatus()
    onClose()
  }

  const handleSend = async () => {
    await sendNft(assetId, recipient.trim().toLowerCase(), memo)
  }

  const openInExplorer = () => {
    if (!nftSendStatus?.txid) return
    setDiscoveryUrl(getExplorerUrl(selectedExplorer, 'tx', nftSendStatus.txid))
    clearNftSendStatus()
    onClose()
    navigate('discovery')
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label={t('common.close')}
        onClick={handleClose}
      />
      <div
        className="relative w-full max-w-md sm:rounded-2xl rounded-t-2xl shadow-xl flex flex-col overflow-hidden mt-auto sm:mt-0"
        style={{ background: '#0c100c', border: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div
          className="flex justify-between items-center px-4 py-3 flex-shrink-0"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
        >
          <h3 className="text-white text-sm font-semibold truncate pr-2">
            {t('nft.sendTitle')} — {nftName}
          </h3>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 text-lg px-2 py-1 flex-shrink-0"
            aria-label={t('common.close')}
          >
            ✕
          </button>
        </div>

        {sent ? (
          <div className="p-4 space-y-4">
            <div className="text-center space-y-2 py-4">
              <div
                className="w-14 h-14 mx-auto rounded-full flex items-center justify-center"
                style={{
                  background: 'rgba(74,222,128,0.12)',
                  border: '1px solid rgba(74,222,128,0.25)',
                }}
              >
                <span className="text-2xl text-success">✓</span>
              </div>
              <p className="text-sm text-white">{t('nft.sentSuccess')}</p>
              <p className="text-xs text-gray-500">
                {nftName} → {recipient}
              </p>
              {nftSendStatus?.txid && (
                <button
                  type="button"
                  onClick={openInExplorer}
                  className="text-[10px] font-mono text-gray-500 underline decoration-dotted break-all px-4"
                >
                  {nftSendStatus.txid}
                </button>
              )}
            </div>
            {nftSendStatus?.txid && (
              <button className="btn-ghost w-full" onClick={openInExplorer}>
                {t('nft.viewInExplorer')}
              </button>
            )}
            <button className="btn-primary w-full" onClick={handleClose}>
              {t('common.done')}
            </button>
          </div>
        ) : (
          <div className="p-4 space-y-4">
            <div className="space-y-1.5">
              <p className="section-label">{t('transfer.recipient')}</p>
              <input
                type="text"
                value={recipient}
                onChange={e => setRecipient(e.target.value.toLowerCase().replace(/[^a-z1-5.]/g, ''))}
                placeholder={t('transfer.recipientPlaceholder')}
                className="input-field"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                maxLength={13}
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <p className="section-label">{t('transfer.memo')}</p>
              <input
                type="text"
                value={memo}
                onChange={e => setMemo(e.target.value)}
                placeholder={t('transfer.memoPlaceholder')}
                className="input-field"
              />
            </div>

            <p className="text-[11px] text-warning leading-snug">⚠️ {t('nft.sendWarning')}</p>

            {errorText && <div className="banner-danger text-danger text-sm">{errorText}</div>}

            {!activeSession && (
              <div
                className="rounded-xl px-3 py-2 text-sm text-warning"
                style={{
                  background: 'rgba(245,158,11,0.08)',
                  border: '1px solid rgba(245,158,11,0.22)',
                }}
              >
                ⚠️ {t('governance.walletLocked')}
              </div>
            )}

            <button
              className="btn-primary w-full py-3"
              onClick={handleSend}
              disabled={sending || !recipient || !activeSession}
            >
              {sending ? t('nft.sending') : t('nft.sendConfirm')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
